import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    debug: false
  },
  db: {
    schema: 'public'
  }
});

// Subscription-related queries
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id,
      name,
      avatar_url,
      username,
      email,
      subscription_tier,
      credits_remaining,
      subscription_start,
      subscription_end,
      subscription_status,
      lemon_subscription_id
    `)
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return data;
};

export const updateProfile = async (userId, updates) => {
  // Handle special case for increment_credits RPC
  if (updates.credits_remaining && typeof updates.credits_remaining === 'object' && updates.credits_remaining.rpc) {
    // This is an RPC call, handle it separately
    try {
      // Extract the result property which contains the RPC call promise
      const { result } = updates.credits_remaining;
      
      // Execute the RPC call and get the result
      const { data: rpcData, error: rpcError } = await result;
      
      if (rpcError) {
        console.error('Error executing RPC for credits:', rpcError);
        return null;
      }
      
      // Remove the RPC object and set the actual value from the RPC result
      const { credits_remaining, ...otherUpdates } = updates;
      const updatedValues = {
        ...otherUpdates,
        credits_remaining: rpcData
      };
      
      // Now update the profile with the new values
      const { data, error } = await supabase
        .from('profiles')
        .update(updatedValues)
        .eq('id', userId)
        .select();
        
      if (error) {
        console.error('Error updating profile after RPC:', error);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error in RPC credit update flow:', error);
      return null;
    }
  }
  
  // Regular update flow
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select();

  if (error) {
    console.error('Error updating profile:', error);
    return null;
  }

  return data;
};

export const consumeCredits = async (userId, amount) => {
  // First get current credits
  const { data: profile, error: fetchError } = await supabase
    .from('profiles')
    .select('credits_remaining')
    .eq('id', userId)
    .single();

  if (fetchError) {
    console.error('Error fetching credits:', fetchError);
    throw new Error('Failed to check credits');
  }

  if (!profile || profile.credits_remaining < amount) {
    throw new Error('Insufficient credits');
  }

  // Update credits
  const { data, error: updateError } = await supabase
    .from('profiles')
    .update({ 
      credits_remaining: profile.credits_remaining - amount,
      updated_at: new Date()
    })
    .eq('id', userId)
    .select();

  if (updateError) {
    console.error('Error updating credits:', updateError);
    throw new Error('Failed to update credits');
  }

  return data;
};

// Helper function to force insert a video record
// This is a fallback method when normal inserts fail
export const forceInsertVideo = async (videoUrl, userId, title = null) => {
  if (!videoUrl || !userId) {
    console.error('Missing required parameters for forceInsertVideo');
    return { error: 'Missing required parameters' };
  }
  
  try {
    // Skip RPC check since it doesn't exist
    // Go directly to insert with minimal fields
    const videoRecord = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substring(2),
      url: videoUrl,
      user_id: userId,
      title: title || `İç Mekan Video ${new Date().toLocaleDateString('tr-TR')}`,
      created_at: new Date().toISOString(),
      type: 'interior_video'
    };
    
    const { data, error: insertError } = await supabase
      .from('videos')
      .insert([videoRecord])
      .select();
      
    if (insertError) {
      console.error('Direct insert failed:', insertError);
      return { error: insertError };
    }
    
    return { success: true, method: 'direct_insert', data };
  } catch (error) {
    console.error('Exception in forceInsertVideo:', error);
    return { error };
  }
};

// Function to refresh the session
const refreshSession = async () => {
  const { data, error } = await supabase.auth.refreshSession();
  if (error) {
    return false;
  }
  return true;
};

// Wrapper function for Supabase API calls
export const supabaseApiCall = async (apiCall) => {
  try {
    const result = await apiCall();
    return result;
  } catch (error) {
    if (error.message === 'JWT expired') {
      const refreshed = await refreshSession();
      if (refreshed) {
        return await apiCall();
      }
    }
    throw error;
  }
};


// Add a function to log request details
export const logRequestDetails = async (endpoint) => {
  const { data: { session } } = await supabase.auth.getSession();
};

export const getApiKey = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    return null;
  }
  return data.session?.access_token || null;
};

// Function to check if custom_agents table exists
export const ensureCustomAgentsTableExists = async () => {
  try {
    // Try to fetch from the table
    const { data, error } = await supabase
      .from('custom_agents')
      .select('id')
      .limit(1);
    
    // If there's an error but it's not a "table doesn't exist" error, it might be permissions
    if (error) {
      console.warn('Error accessing custom_agents table:', error);
      
      if (error.code === '42P01') {
        console.warn('The custom_agents table does not exist. Please contact the administrator.');
        return false;
      } else {
        // For other errors, we'll assume the table exists but we don't have proper permissions
        console.warn('You may not have proper permissions to access the custom_agents table.');
        return true;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking custom_agents table:', error);
    // In case of unexpected errors, we'll assume the table exists
    return true;
  }
};

// Function to check if chat tables exist
export const ensureChatTablesExist = async () => {
  try {
    // Try to fetch tables list first to check if the table exists
    const { data: tables, error: tablesError } = await supabase
      .from('chat_sessions')
      .select('id')
      .limit(1);
    
    // If there's an error but it's not a "table doesn't exist" error, it might be permissions
    if (tablesError) {
      console.warn('Error accessing chat_sessions table:', tablesError);
      
      if (tablesError.code === '42P01') {
        console.warn('The chat_sessions table does not exist. Please contact the administrator.');
        return false;
      } else {
        // For other errors, we'll assume the table exists but we don't have proper permissions
        console.warn('You may not have proper permissions to access the chat_sessions table.');
        return true;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking chat tables:', error);
    // In case of unexpected errors, we'll assume the table exists
    return true;
  }
};

// Chat-related functions
export const saveChatSession = async (userId, title, lastMessage) => {
  try {
    const { data, error } = await supabase
      .from('chat_sessions')
      .insert({
        user_id: userId,
        title: title || `Sohbet ${new Date().toLocaleString()}`,
        last_message: lastMessage || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving chat session:', error);
    throw error;
  }
};

export const updateChatSession = async (sessionId, updates) => {
  try {
    const { data, error } = await supabase
      .from('chat_sessions')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating chat session:', error);
    throw error;
  }
};

export const saveChatMessages = async (sessionId, userId, messages) => {
  try {
    const messagesToSave = messages.map(msg => ({
      session_id: sessionId,
      user_id: userId,
      text: msg.text,
      sender: msg.sender,
      timestamp: msg.timestamp.toISOString(),
      has_file: !!msg.file,
      chart_data: msg.chartData || null
    }));
    
    const { data, error } = await supabase
      .from('chat_messages')
      .insert(messagesToSave)
      .select();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving chat messages:', error);
    throw error;
  }
};

export const saveFileForMessage = async (messageId, file) => {
  try {
    // If file is already a URL, just update the message
    if (typeof file.content === 'string' && file.content.startsWith('http')) {
      const { error } = await supabase
        .from('chat_messages')
        .update({
          file_path: file.content,
          file_type: file.type,
          file_name: file.name
        })
        .eq('id', messageId);
      
      if (error) throw error;
      return file.content;
    }
    
    // If it's a data URL, extract the base64 part for storage
    let fileContent = file.content;
    if (typeof file.content === 'string' && file.content.startsWith('data:')) {
      // Store just the base64 part to save space
      const base64Data = file.content.split(',')[1];
      fileContent = base64Data; // fileContent now holds raw base64
    }

    // Determine if we should store base64 directly or upload to storage
    // Store directly if it's base64 and reasonably small (e.g., < 500KB)
    // OR if it's a document type that Gemini needs to read directly.
    const shouldStoreBase64Directly = typeof fileContent === 'string' &&
      !fileContent.startsWith('http') && // Ensure it's not already a URL
      (fileContent.length < 500000 || // Size limit check
       file.type.startsWith('application/') || // PDF, DOCX etc.
       file.type.startsWith('text/')); // TXT

    if (shouldStoreBase64Directly) {
      // Ensure we are storing only the raw base64 data
      let rawBase64Data = fileContent;
      // The check `if (rawBase64Data.startsWith('data:'))` is redundant here
      // because we already extracted the raw base64 above if it was a data URL.

      const { error } = await supabase
        .from('chat_messages')
        .update({
          file_path: rawBase64Data, // Store raw base64 directly
          file_type: file.type,
          file_name: file.name,
          has_file: true // Ensure has_file is true
        })
        .eq('id', messageId);

      if (error) throw error;
      // Return the raw base64 data (or reconstruct data URL if needed by caller, but raw is fine here)
      return rawBase64Data;
    }
    
    // For larger files, upload to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `chat-files/${messageId}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('chat-files')
      .upload(fileName, file.content);
    
    if (uploadError) throw uploadError;
    
    const { data: { publicUrl } } = supabase.storage
      .from('chat-files')
      .getPublicUrl(fileName);
    
    // Update message with file path
    const { error } = await supabase
      .from('chat_messages')
      .update({
        file_path: publicUrl,
        file_type: file.type,
        file_name: file.name
      })
      .eq('id', messageId);
    
    if (error) throw error;
    return publicUrl;
  } catch (error) {
    console.error('Error saving file for message:', error);
    throw error;
  }
};

export const saveImagesForMessage = async (messageId, images) => {
  try {
    const imagesToSave = images.map(url => ({
      message_id: messageId,
      url: url,
      prompt: '',
      created_at: new Date().toISOString()
    }));
    
    const { data, error } = await supabase
      .from('chat_images')
      .insert(imagesToSave);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving images for message:', error);
    throw error;
  }
};

export const fetchChatSessions = async (userId) => {
  try {
    // Try to fetch chat sessions directly
    const { data, error } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('is_pinned', { ascending: false })
      .order('updated_at', { ascending: false });
    
    if (error) {
      // If there's an error, log it but return an empty array instead of throwing
      console.error('Error fetching chat sessions:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Unexpected error fetching chat sessions:', error);
    // Return empty array instead of throwing to prevent app crashes
    return [];
  }
};

export const fetchChatMessages = async (sessionId) => {
  try {
    // Fetch messages
    const { data: messagesData, error: messagesError } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('timestamp', { ascending: true });
    
    if (messagesError) throw messagesError;
    
    // Fetch images for messages
    const messageIds = messagesData.map(msg => msg.id);
    
    let imagesData = [];
    if (messageIds.length > 0) {
      const { data: imgData, error: imagesError } = await supabase
        .from('chat_images')
        .select('*')
        .in('message_id', messageIds);
      
      if (imagesError) throw imagesError;
      imagesData = imgData || [];
    }
    
    // Combine messages with their images
    const messages = messagesData.map(msg => {
      const msgImages = imagesData
        .filter(img => img.message_id === msg.id)
        .map(img => img.url);
      
      // Process file content if it exists
      let fileContent = msg.file_path;
      
      // Reconstruct data URL if file_path contains raw base64 data
      if (msg.has_file && fileContent && msg.file_type && !fileContent.startsWith('http') && !fileContent.startsWith('data:')) {
        // Check if it looks like base64 (simple check for common characters)
        // You might want a more robust check depending on your data
        const looksLikeBase64 = /^[A-Za-z0-9+/=]+$/.test(fileContent.substring(0, 100));
        
        if (looksLikeBase64 || fileContent.startsWith('/9j/') || fileContent.startsWith('iVBOR')) {
           // Assume it's raw base64, reconstruct the data URL
           fileContent = `data:${msg.file_type};base64,${fileContent}`;
        }
        // If it doesn't look like base64 and isn't a URL/data URL, it might be corrupted or an old format.
        // Keep fileContent as is for now, the component might handle it or show an error.
      }
      
      return {
        id: msg.id,
        text: msg.text,
        sender: msg.sender,
        timestamp: new Date(msg.timestamp),
        file: msg.has_file ? {
          name: msg.file_name,
          type: msg.file_type,
          content: fileContent
        } : null,
        images: msgImages.length > 0 ? msgImages : undefined,
        chartData: msg.chart_data || null // Include chart data if it exists
      };
    });
    
    return messages;
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error;
  }
};

// Helper function to get public URL with optional download and transform options
export const getPublicUrl = (bucketName, filePath, options = {}) => {
  try {
    const { data } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath, options);
    
    return data?.publicUrl || null;
  } catch (error) {
    console.error('Error getting public URL:', error);
    return null;
  }
};

// Helper function to fetch base64 content from a URL
export const fetchBase64Content = async (url, fileType = 'image/png') => {
  try {
    // First try with fetch API and specific headers
    const response = await fetch(url, {
      headers: {
        'Accept-Encoding': 'identity'
      },
      cache: 'no-cache'
    });
    
    const contentType = response.headers.get('content-type');
    
    // If it's already an image type, return the URL directly
    if (contentType && contentType.includes('image/')) {
      return { isUrl: true, content: url };
    }
    
    // Otherwise get the text content
    const text = await response.text();
    
    // Check if it's base64 data
    if (text && (
      text.startsWith('/9j/') || 
      text.startsWith('iVBOR') || 
      text.includes('data:image') ||
      /^[A-Za-z0-9+/=]+$/.test(text.substring(0, 100))
    )) {
      // If it already has the data:image prefix
      if (text.startsWith('data:image')) {
        return { isUrl: false, content: text };
      } else {
        // Return with proper data URL format
        return { isUrl: false, content: `data:${fileType};base64,${text}` };
      }
    }
    
    // If we couldn't identify it as base64, return the original URL
    return { isUrl: true, content: url };
  } catch (error) {
    console.error('Error fetching base64 content:', error);
    return { isUrl: true, content: url }; // Return original URL on error
  }
};
